import React, { memo } from 'react';
import { Direction, LocationMark, VerticalLine, Website } from '@yme/assets/icons';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { Tooltip, TooltipPlacement } from '@yme/atoms/tooltip';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack300, colorCoreBlack900Base } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularLabelLevel1, RegularTextLevel4 } from '@yme/tokens/typography';
import { DigitalDataMutation, formatDistance, getWebsiteAddress, onOpenWindow, setDigitalData, } from '@yme/utils/common';
import { BLANK, SELF } from '@yme/utils/constants';
import { getDirectionLink, getEmailAddress, getPhoneNumbers } from './helpers';
import { CardBodyStyled, CardFooterStyled, CardHeaderRowStyled, CardItemStyled, RegularLinkLevel2Styled, SVGIconDividerStyled, SVGIconStyled, } from './styled';
function DealerCardCarouselComponent(props) {
    const { cardProps, labels = {}, isRightToLeft, searchedPlace = [] } = props;
    const { name, vicinityId: id, distance, addressLines, email, websites, telephoneNumbers, zipCode, city, isGBCountyRoot, } = cardProps;
    const addressDealer = isGBCountyRoot
        ? `${addressLines && `${addressLines[0]},`} ${city} ${zipCode}`
        : `${addressLines && `${addressLines[0]},`} ${zipCode} ${city}`;
    const websiteAddress = getWebsiteAddress(websites);
    const { t } = useTranslation();
    const placementDirection = isRightToLeft
        ? TooltipPlacement.ABOVE_RIGHT
        : TooltipPlacement.ABOVE_LEFT;
    const myLocation = searchedPlace?.latitude && searchedPlace?.longitude
        ? `${searchedPlace?.latitude},${searchedPlace?.longitude}`
        : '';
    const directionLink = addressLines && addressLines[0]
        ? getDirectionLink({
            destination: `${addressLines[0]} ${zipCode} ${city}`,
            myLocation,
            startingPlace: searchedPlace.value,
        })
        : null;
    const emailLink = email && getEmailAddress(email);
    const phoneNumber = telephoneNumbers && getPhoneNumbers(telephoneNumbers);
    const onEmailClick = () => {
        return () => {
            if (!emailLink)
                return;
            setDigitalData(DigitalDataMutation.DEALER_LOCATOR_CONTACT, {
                id,
                name,
                contact: 'email',
            });
            onOpenWindow(emailLink, BLANK);
        };
    };
    const onPhoneClick = () => {
        return () => {
            if (!phoneNumber)
                return;
            setDigitalData(DigitalDataMutation.DEALER_LOCATOR_CONTACT, {
                id,
                name,
                contact: 'tel',
            });
            onOpenWindow(phoneNumber.link, SELF);
        };
    };
    const onWebsiteClick = () => {
        return () => {
            if (!websiteAddress)
                return;
            setDigitalData(DigitalDataMutation.DEALER_LOCATOR_CONTACT, {
                id,
                name,
                contact: 'http',
            });
            onOpenWindow(websiteAddress, BLANK);
        };
    };
    const onRouteClick = () => {
        return () => {
            if (!directionLink)
                return;
            setDigitalData(DigitalDataMutation.DEALER_LOCATOR_PLAN_A_ROUTE, {
                id,
                name,
                contact: '',
            });
            onOpenWindow(directionLink, BLANK);
        };
    };
    return (React.createElement(CardItemStyled, { isRightToLeft: isRightToLeft },
        React.createElement(CardBodyStyled, null,
            React.createElement(CardHeaderRowStyled, null,
                React.createElement(Tooltip, { title: name.toLocaleUpperCase(), hasEnableHoverListener: true, placement: placementDirection },
                    React.createElement(RegularTextLevel4, { weight: 500, isEllipsisEndingLine: true, isUppercase: true }, name)),
                React.createElement(FlexWrapper, { alignItems: "center", height: "20px" }, Boolean(distance) && (React.createElement(React.Fragment, null,
                    React.createElement(RegularLabelLevel1, { weight: 500 }, formatDistance(distance, t)),
                    React.createElement(SVGIcon, { type: LocationMark }))))),
            React.createElement(FlexWrapper, { mtMobile: "10px", mtTablet: "14px" },
                React.createElement(RegularTextLevel4, { color: colorCoreBlack300, isUppercase: true, isEllipsisEndingLine: true }, addressDealer)),
            emailLink && (React.createElement(FlexWrapper, { mt: "2px" },
                React.createElement(RegularLinkLevel2Styled, { as: "button", color: colorCoreBlack900Base, isEllipsisEndingLine: true, onClick: onEmailClick() }, email))),
            phoneNumber?.link && (React.createElement(FlexWrapper, { mt: "2px" },
                React.createElement(RegularLinkLevel2Styled, { as: "button", color: colorCoreBlack900Base, isEllipsisEndingLine: true, onClick: onPhoneClick() }, phoneNumber.value)))),
        React.createElement(CardFooterStyled, null,
            websiteAddress && (React.createElement(FlexWrapper, { alignItems: "center" },
                React.createElement(SVGIconStyled, { type: Website, isRightToLeft: isRightToLeft }),
                React.createElement(RegularLinkLevel2Styled, { as: "button", color: colorCoreBlack900Base, onClick: onWebsiteClick() }, labels?.dealerLocator_visitTheWebsite_link))),
            websiteAddress && (React.createElement(FlexWrapper, { ml: "8px", mr: "8px", alignItems: "center" },
                React.createElement(SVGIconDividerStyled, { type: VerticalLine }))),
            directionLink && (React.createElement(FlexWrapper, { alignItems: "center" },
                React.createElement(SVGIconStyled, { type: Direction, isRightToLeft: isRightToLeft }),
                React.createElement(RegularLinkLevel2Styled, { as: "button", color: colorCoreBlack900Base, onClick: onRouteClick() }, labels?.dealerLocator_getDirections_link))))));
}
export const DealerCardCarousel = memo(DealerCardCarouselComponent);
DealerCardCarousel.displayName = 'DealerCardCarousel_Molecules';
